// ---------- le chemin d'un chapitre : où poser les parties, et l'allée qui les relie ----------
//
// Un serpentin en rangées, dans un cadre de 390 de large (la maquette « J ») : quatre parties sur
// la première rangée, trois sur les suivantes, de gauche à droite puis de droite à gauche. Dix
// parties tiennent en trois rangées et 400 de haut : tout le chapitre se voit sans défiler, et on
// lit d'un coup où l'on est et ce qui reste. Un chapitre plus long ajoute des rangées.
//
// La géométrie est calculée ici, une fois, et dessinée en coordonnées du cadre : le SVG et les
// tuiles se mettent ensuite à l'échelle de l'écran ensemble (viewBox d'un côté, % de l'autre).

export const MAP_W = 390;
const ROW_H = 140;
const TOP = 60;
const RIGHT = 310;
const LEFT = 100;

export interface MapGeometry {
  height: number;
  /** le centre de chaque partie, dans l'ordre du chemin */
  nodes: { x: number; y: number }[];
  /** l'allée, en `d` de SVG */
  road: string;
  /** la place de chaque partie le long de l'allée, sur 1000 (pour l'or du parcouru) */
  along: number[];
}

/** Les x d'une rangée, dans le sens où on la parcourt. */
function rowXs(count: number, ltr: boolean, first: boolean): number[] {
  const xs = first ? [70, 150, 230, 310] : ltr ? [140, 220, 315] : [290, 195, 100];
  const out = xs.slice(0, count);
  return first && !ltr ? out.reverse() : out;
}

export function mapGeometry(n: number): MapGeometry {
  const nodes: { x: number; y: number }[] = [];
  const rows: { y: number; ltr: boolean }[] = [];
  for (let left = n, k = 0; left > 0; k++) {
    const count = Math.min(left, k === 0 ? 4 : 3);
    const ltr = k % 2 === 0;
    const y = TOP + k * ROW_H;
    rows.push({ y, ltr });
    for (const x of rowXs(count, ltr, k === 0)) nodes.push({ x, y });
    left -= count;
  }
  // L'allée : une ligne par rangée, et un demi-tour arrondi au bout, côté droit puis côté gauche.
  const segs: string[] = [`M 20 ${TOP}`];
  rows.forEach((r, k) => {
    const last = k === rows.length - 1;
    const endX = last ? (r.ltr ? 365 : 25) : r.ltr ? RIGHT : LEFT;
    segs.push(`L ${endX} ${r.y}`);
    if (!last) {
      const bx = r.ltr ? 380 : 30;
      segs.push(`C ${bx} ${r.y} ${bx} ${r.y + ROW_H} ${endX} ${r.y + ROW_H}`);
    }
  });
  const road = segs.join(' ');
  return { height: TOP + (rows.length - 1) * ROW_H + 60, nodes, road, along: alongOf(road, nodes) };
}

/** Échantillonne l'allée (lignes et courbes de Bézier) et rend, pour chaque partie, sa place le
 *  long du trajet sur 1000 — c'est ce qui dit jusqu'où peindre l'or (`stroke-dasharray`). */
function alongOf(d: string, nodes: { x: number; y: number }[]): number[] {
  const t = d.split(' ');
  const pts: [number, number][] = [];
  let x = 0;
  let y = 0;
  for (let i = 0; i < t.length;) {
    const op = t[i++];
    if (op === 'M') {
      x = +t[i++];
      y = +t[i++];
      pts.push([x, y]);
    } else if (op === 'L') {
      const nx = +t[i++];
      const ny = +t[i++];
      for (let s = 1; s <= 40; s++) pts.push([x + ((nx - x) * s) / 40, y + ((ny - y) * s) / 40]);
      x = nx;
      y = ny;
    } else if (op === 'C') {
      const c = t.slice(i, i + 6).map(Number);
      i += 6;
      for (let s = 1; s <= 40; s++) {
        const u = s / 40;
        const v = 1 - u;
        pts.push([
          v * v * v * x + 3 * v * v * u * c[0] + 3 * v * u * u * c[2] + u * u * u * c[4],
          v * v * v * y + 3 * v * v * u * c[1] + 3 * v * u * u * c[3] + u * u * u * c[5],
        ]);
      }
      x = c[4];
      y = c[5];
    }
  }
  const acc = [0];
  for (let i = 1; i < pts.length; i++)
    acc.push(acc[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  const total = acc[acc.length - 1] || 1;
  return nodes.map((n) => {
    let best = 0;
    for (let i = 1; i < pts.length; i++)
      if (
        Math.hypot(pts[i][0] - n.x, pts[i][1] - n.y) <
        Math.hypot(pts[best][0] - n.x, pts[best][1] - n.y)
      )
        best = i;
    return Math.round((acc[best] / total) * 1000);
  });
}
